// src/liveUpdates/DataRefreshProvider.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { api } from "./lib/http";


const DataRefreshContext = createContext({ version: 0, lastId: 0 });
export const useDataRefresh = () => useContext(DataRefreshContext);

export const DataRefreshProvider = ({ apiBase = "https://shopaholics.ge/api", children }) => {
  const [version, setVersion] = useState(0);
  const [lastId, setLastId] = useState(() =>
    Number(localStorage.getItem("db_last_change_id") || 0)
  );

  // ---- Refs
  const lastIdRef = useRef(lastId);
  const esRef = useRef(null);
  const pollTimer = useRef(null);
  const sseRetryTimer = useRef(null);
  const busyRef = useRef(false);

  // backoff + heartbeat
  const backoffRef = useRef(1000);       // 1s -> 2s -> 4s ... capped 15s
  const HEARTBEAT_MS = 30000;            // თუ 30წმ არაფერი მოვიდა — გადავივრთოთ
  const lastMsgAtRef = useRef(Date.now());
  const hbTimerRef = useRef(null);

  // burst-safe bump (leading + trailing)
  const lastBumpRef = useRef(0);
  const bumpTrailingRef = useRef(null);
  const bump = () => {
    const now = Date.now();
    const WINDOW = 400;
    if (now - lastBumpRef.current > WINDOW) {
      lastBumpRef.current = now;
      setVersion((v) => v + 1);
    } else {
      clearTimeout(bumpTrailingRef.current);
      bumpTrailingRef.current = setTimeout(() => {
        lastBumpRef.current = Date.now();
        setVersion((v) => v + 1);
      }, WINDOW + 50);
    }
  };

  // keep lastId in ref
  useEffect(() => {
    lastIdRef.current = lastId;
  }, [lastId]);

  // persist + cross-tab ping
  useEffect(() => {
    localStorage.setItem("db_last_change_id", String(lastId));
    localStorage.setItem("db_last_change_ping", String(Date.now()));
  }, [lastId]);

  // ---- Helpers
  const applyChangePacket = (packet) => {
    if (!packet || typeof packet.lastId !== "number") return;
    if (packet.lastId > lastIdRef.current) {
      setLastId(packet.lastId);
      bump();
    } else {
      bump();
    }
    lastMsgAtRef.current = Date.now();
    backoffRef.current = 1000; // წარმატებაზე backoff reset
    stopPoll();                // მოვიდა ცვლილება → სჯობს ისევ SSE-ზე ვიყოთ
  };

  const pollOnce = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const cursor = Number(localStorage.getItem("db_last_change_id") || 0);
      const { data } = await api.get(
        `${apiBase}/changes_poll.php?cursor=${cursor}`,
        { timeout: 8000 }
      );
      if (data && typeof data.lastId === "number") {
        applyChangePacket(data);
      }
    } catch {
      // ჩუმად
    } finally {
      busyRef.current = false;
    }
  };

  const startPoll = (intervalMs = 3000) => {
    if (pollTimer.current) return;
    const loop = async () => {
      await pollOnce();
      const hidden = document.hidden;
      const next = hidden ? Math.max(intervalMs, 15000) : intervalMs;
      pollTimer.current = setTimeout(loop, next);
    };
    loop();
  };

  const stopPoll = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const clearHB = () => {
    if (hbTimerRef.current) {
      clearTimeout(hbTimerRef.current);
      hbTimerRef.current = null;
    }
  };

  const armHB = () => {
    clearHB();
    hbTimerRef.current = setTimeout(() => {
      // თუ დიდი ხანია არაფერი მოსულა — გადააბრუნე კავშირი
      if (Date.now() - lastMsgAtRef.current >= HEARTBEAT_MS) {
        reopenSSEWithBackoff();
      } else {
        armHB();
      }
    }, HEARTBEAT_MS + 500);
  };

  const closeSSE = () => {
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {}
      esRef.current = null;
    }
    clearHB();
  };

  const scheduleSSERetry = (delay) => {
    clearTimeout(sseRetryTimer.current);
    sseRetryTimer.current = setTimeout(() => {
      sseRetryTimer.current = null;
      openSSE(0);
    }, delay);
  };

  const reopenSSEWithBackoff = () => {
    closeSSE();
    const delay = Math.min(backoffRef.current, 15000);
    backoffRef.current = Math.min(delay * 2, 15000);
    // პარალელურად poll არ გავჩერდეს, UI იყოს ცოცხალი
    if (!pollTimer.current) startPoll(3000);
    scheduleSSERetry(delay);
  };

  /**
   * გახსენი SSE (სურვილისამებრ დელეით).
   * გაშვებამდე ვამოწმებთ online/visibility-ს, რომ cold start/პიჩევის დროს არ აფეთქდეს.
   */
  const openSSE = (delayMs = 1200) => {
    closeSSE();

    const run = () => {
      // თუ ტაბი დამალულია ან offline ვარ, არ დავიწყოთ — წავიდეთ poll-ზე
      if (document.hidden || !navigator.onLine) {
        if (!pollTimer.current) startPoll(3000);
        return;
      }

      try {
        const cursor = Number(localStorage.getItem("db_last_change_id") || 0);
        const url = `${apiBase}/changes_stream.php?cursor=${cursor}`;
        const es = new EventSource(url, { withCredentials: false });
        esRef.current = es;
        lastMsgAtRef.current = Date.now();
        armHB();

        es.addEventListener("hello", (e) => {
          try {
            const p = JSON.parse(e.data);
            if (typeof p.lastId === "number") {
              setLastId(p.lastId);
              bump();
              lastMsgAtRef.current = Date.now();
              backoffRef.current = 1000;
            }
          } catch {}
        });

        es.addEventListener("change", (e) => {
          try {
            const p = JSON.parse(e.data);
            applyChangePacket(p);
          } catch {}
        });

        es.onmessage = () => {
          lastMsgAtRef.current = Date.now();
          backoffRef.current = 1000;
        };

        es.onerror = () => {
          // MIME „text/html“, suspended, closed და სხვაზე: მშვიდად fallback + backoff
          reopenSSEWithBackoff();
        };
      } catch {
        reopenSSEWithBackoff();
      }
    };

    // პატარა დელეი “ღვიძების” / cold start-ის შემდეგ, რომ ქსელი დადგეს
    setTimeout(run, delayMs);
  };

  // ---- Mount / Unmount
  useEffect(() => {
    // პირველი გაშვებისას: ცოტა დავიცადოთ, რომ ქსელი დადგეს
    openSSE(1200);

    const onBeforeUnload = () => closeSSE();
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      closeSSE();
      stopPoll();
      clearTimeout(sseRetryTimer.current);
      clearTimeout(bumpTrailingRef.current);
    };
    // eslint-disable-next-line
  }, []);

  // cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "db_last_change_id") {
        const ext = Number(localStorage.getItem("db_last_change_id") || 0);
        if (ext > lastIdRef.current) {
          setLastId(ext);
          bump();
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // visibility/focus/network wake-up
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) {
        // დაბრუნდა ტაბი
        pollOnce();
        stopPoll();
        openSSE(800); // მცირე დელეი
      } else {
        // ფონის ტაბი — დაიცავი რესურსები
        closeSSE();
        if (!pollTimer.current) startPoll(15000);
      }
    };
    const onFocus = () => {
      pollOnce();
      openSSE(600);
    };
    const onOnline = () => {
      pollOnce();
      openSSE(800);
    };
    const onOffline = () => {
      closeSSE();
      if (!pollTimer.current) startPoll(5000);
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return (
    <DataRefreshContext.Provider value={{ version, lastId }}>
      {children}
    </DataRefreshContext.Provider>
  );
};
